import React, { useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Link,
  Alert,
  Divider,
  useTheme,
  useMediaQuery,
  Checkbox,
  FormControlLabel,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import { 
  ChevronRight, 
  CheckCircle as CheckCircleIcon,
  Menu as MenuIcon,
  Close as CloseIcon,
  LockOutlined
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../services/api';

interface LoginProps {
  onToggleMode: () => void;
}

export const Login: React.FC<LoginProps> = ({ onToggleMode }: LoginProps) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordMessage, setForgotPasswordMessage] = useState('');
  const [aboutOpen, setAboutOpen] = useState(false);
  
  const { login } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      await login(email, password);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setForgotPasswordMessage('');
    setLoading(true);

    try {
      const response: any = await apiService.forgotPassword(email);
      let message = response.message || 'If an account with that email exists, a temporary password has been sent.';
      
      if (response.developmentInfo && response.developmentInfo.temporaryPassword) {
        message += `\n\nDEVELOPMENT MODE: Your temporary password is: ${response.developmentInfo.temporaryPassword}`;
      }
      
      setForgotPasswordMessage(message);
      setShowForgotPassword(false);
    } catch (err: any) {
      setError(err.message || 'Failed to send reset email');
    } finally {
      setLoading(false);
    }
  };

  const toggleAbout = () => setAboutOpen(!aboutOpen);

  // Custom styles for Dark Mode inputs
  const darkInputStyles = {
    '& .MuiInputLabel-root': { color: '#a1a1aa' }, // zinc-400
    '& .MuiOutlinedInput-root': {
      color: '#fff',
      backgroundColor: '#27272a', // zinc-800
      '& fieldset': { borderColor: 'transparent' },
      '&:hover fieldset': { borderColor: '#3f3f46' }, // zinc-700
      '&.Mui-focused fieldset': { borderColor: '#fff' },
    },
    '& .MuiInputBase-input': {
      color: '#fff',
    }
  };

  return (
    <Box component="main" sx={{ 
      minHeight: '100vh', 
      backgroundColor: '#09090b', // zinc-950 (Almost black)
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      p: 2,
      position: 'relative'
    }}>
      
      {/* Top Right "About" Menu Button */}
      <Box sx={{ position: 'absolute', top: 24, right: 24, zIndex: 1000 }}>
        <Button
          startIcon={<MenuIcon />}
          onClick={toggleAbout}
          sx={{ 
            textTransform: 'none', 
            color: '#a1a1aa', // zinc-400
            fontWeight: 500,
            '&:hover': { color: '#fff' }
          }}
        >
          About
        </Button>
      </Box>

      {/* Centered Card */}
      <Box sx={{ 
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        width: '100%',
        maxWidth: '900px',
        backgroundColor: '#18181b', // zinc-900 (Dark card bg)
        borderRadius: '24px',
        overflow: 'hidden',
        border: '1px solid #27272a', // zinc-800
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)'
      }}>
        
        {/* Left Side - Login Form */}
        <Box sx={{ flex: 1, p: { xs: 4, md: 6 } }}>
          <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 1 }}>
             <LockOutlined sx={{ color: '#fff', fontSize: 20 }} />
             <Typography variant="h6" sx={{ fontWeight: 700, color: '#fff' }}>
              Sign in
            </Typography>
          </Box>

          <Typography variant="body2" sx={{ color: '#a1a1aa', mb: 4 }}>
            Welcome back. Log in to continue tracking.
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 3, borderRadius: 1, bgcolor: '#450a0a', color: '#fecaca' }}>
              {error}
            </Alert>
          )}

          {forgotPasswordMessage && (
            <Alert severity="success" sx={{ mb: 3, borderRadius: 1, whiteSpace: 'pre-wrap', bgcolor: '#052e16', color: '#bbf7d0' }}>
              {forgotPasswordMessage}
            </Alert>
          )}

          {showForgotPassword ? (
            <Box component="form" onSubmit={handleForgotPassword}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="caption" sx={{ color: '#fff', mb: 1, display: 'block' }}>Email</Typography>
                <TextField
                  required
                  fullWidth
                  placeholder="you@company.com"
                  id="email"
                  name="email"
                  autoComplete="email"
                  autoFocus
                  value={email}
                  onChange={(e: any) => setEmail(e.target.value)}
                  variant="outlined"
                  sx={darkInputStyles}
                  InputLabelProps={{ shrink: false }} 
                />
              </Box>
              
              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading}
                sx={{
                  py: 1.5,
                  backgroundColor: '#27272a',
                  color: 'white',
                  textTransform: 'none',
                  fontWeight: 600,
                  '&:hover': { backgroundColor: '#3f3f46' },
                  mb: 2,
                  borderRadius: '8px'
                }}
              >
                {loading ? 'Sending...' : 'Send Temporary Password'}
              </Button>
              <Button
                fullWidth
                onClick={() => {
                  setShowForgotPassword(false);
                  setError('');
                  setForgotPasswordMessage('');
                }}
                sx={{ textTransform: 'none', color: '#a1a1aa', '&:hover': { color: '#fff' } }}
              >
                Back to Sign In
              </Button>
            </Box>
          ) : (
            <Box component="form" onSubmit={handleSubmit}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="caption" sx={{ color: '#fff', mb: 1, display: 'block' }}>Email</Typography>
                <TextField
                  required
                  fullWidth
                  id="email"
                  placeholder="you@company.com"
                  name="email"
                  autoComplete="email"
                  autoFocus
                  value={email}
                  onChange={(e: any) => setEmail(e.target.value)}
                  variant="outlined"
                  sx={darkInputStyles}
                />
              </Box>
              
              <Box sx={{ mb: 3 }}>
                 <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="caption" sx={{ color: '#fff' }}>Password</Typography>
                    <Link
                      component="button"
                      variant="caption"
                      onClick={(e: any) => {
                        e.preventDefault();
                        setShowForgotPassword(true);
                        setError('');
                        setForgotPasswordMessage('');
                      }}
                      sx={{ 
                        textDecoration: 'none', 
                        color: '#a1a1aa', // zinc-400
                        '&:hover': { color: '#fff', textDecoration: 'underline' } 
                      }}
                    >
                      Forgot?
                    </Link>
                 </Box>
                <TextField
                  required
                  fullWidth
                  name="password"
                  placeholder="••••••••"
                  type="password"
                  id="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e: any) => setPassword(e.target.value)}
                  variant="outlined"
                  sx={darkInputStyles}
                />
              </Box>

              <Box sx={{ mb: 3 }}>
                <FormControlLabel
                  control={
                    <Checkbox 
                      value="remember" 
                      sx={{ 
                        color: '#3f3f46',
                        '&.Mui-checked': { color: '#fff' }
                      }}
                      checked={rememberMe}
                      onChange={(e: any) => setRememberMe(e.target.checked)}
                    />
                  }
                  label={<Typography variant="body2" sx={{ color: '#a1a1aa' }}>Remember me</Typography>}
                />
              </Box>

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading}
                endIcon={!loading && <ChevronRight />}
                sx={{
                  py: 1.5,
                  backgroundColor: '#27272a', // Zinc-800
                  color: 'white',
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: '8px',
                  border: '1px solid #3f3f46',
                  '&:hover': { backgroundColor: '#3f3f46' }, // Zinc-700
                  mb: 4
                }}
              >
                {loading ? 'Signing In...' : 'Sign in'}
              </Button>

              <Typography variant="body2" color="#a1a1aa">
                New here?{' '}
                <Link
                  component="button"
                  variant="body2"
                  onClick={(e: any) => {
                    e.preventDefault();
                    onToggleMode();
                  }}
                  sx={{ 
                    color: '#fff', 
                    fontWeight: 500,
                    textDecoration: 'underline',
                    textUnderlineOffset: '4px',
                    '&:hover': { color: '#e4e4e7' }
                  }}
                >
                  Create an account
                </Link>
              </Typography>
            </Box>
          )}
        </Box>

        {/* Right Side - Info Panel */}
        <Box
          sx={{
            flex: 1,
            display: { xs: 'none', md: 'flex' },
            flexDirection: 'column',
            justifyContent: 'center',
            borderLeft: '1px solid #27272a',
            backgroundColor: '#18181b', // Same dark bg
            p: 6
          }}
        >
          <Box sx={{ maxWidth: '400px', mx: 'auto' }}>
            <Box sx={{ mb: 6 }}>
              <Typography variant="h6" sx={{ color: '#a1a1aa', fontWeight: 500, mb: 3, fontSize: '0.9rem' }}>
                Why TrackerV2?
              </Typography>
              
              <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
                {[
                  'See what you actually worked on – automatic screenshots',
                  'Your team sees your progress – visual transparency',
                  'Understand what teammates do – no more guessing',
                  'Desktop app included – Mac & Windows widget',
                  'Chat & Huddle – communicate without switching tools'
                ].map((item, index) => (
                  <Box component="li" key={index} sx={{ display: 'flex', alignItems: 'start', gap: 1.5 }}>
                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#ffd369', flexShrink: 0, mt: 1 }} />
                    <Typography variant="body2" sx={{ color: '#e4e4e7' }}>
                      {item}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>

            <Divider sx={{ mb: 6, borderColor: '#27272a' }} />

            <Box sx={{ mb: 6 }}>
              <Typography variant="h6" sx={{ color: '#a1a1aa', fontWeight: 500, mb: 2, fontSize: '0.9rem' }}>
                Your data
              </Typography>
              <Typography variant="body2" sx={{ color: '#e4e4e7', lineHeight: 1.6 }}>
                Stored in secure infrastructure. You can delete screenshots and export your data anytime.
              </Typography>
            </Box>

            <Divider sx={{ mb: 6, borderColor: '#27272a' }} />

            <Typography variant="caption" sx={{ color: '#71717a' }}> {/* zinc-500 */}
              By continuing you agree to our{' '}
              <Link href="#" color="inherit" underline="hover">Terms</Link>
              {' '}and{' '}
              <Link href="#" color="inherit" underline="hover">Privacy Policy</Link>.
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* About Drawer - Light Mode */}
      <Drawer
        anchor="right"
        open={aboutOpen}
        onClose={toggleAbout}
        PaperProps={{
          sx: { 
            width: { xs: '100%', sm: 400 }, 
            p: 4,
            backgroundColor: '#ffffff',
            color: '#1a1a1a'
          }
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: '#1a1a1a' }}>
            About TrackerV2
          </Typography>
          <IconButton onClick={toggleAbout} sx={{ color: '#666' }}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ mb: 4 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: '#666' }}>
            Your work, visible to you and your team
          </Typography>
          <Typography variant="body2" sx={{ lineHeight: 1.6, mb: 2, color: '#1a1a1a' }}>
            Screenshot-based time tracking that shows what you actually do. AI analyzes your work automatically – no manual entry needed.
          </Typography>
          <List dense>
            {[
              '📸 For you: See exactly where your time went – your personal work diary',
              '👁️ For your team: Visual transparency when you log time',
              '🖥️ Desktop App: Small widget for Mac & Windows – track with one click',
              '💬 Chat & Huddle: Message your team or jump into live video conversations'
            ].map((text, i) => (
              <ListItem key={i} sx={{ px: 0 }}>
                 <ListItemText primary={text} primaryTypographyProps={{ color: '#1a1a1a', variant: 'body2', sx: { lineHeight: 1.6 } }} />
              </ListItem>
            ))}
          </List>
        </Box>

        <Divider sx={{ mb: 4 }} />

        <Box sx={{ mb: 4 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1, color: '#1a1a1a' }}>
            <CheckCircleIcon fontSize="small" sx={{ color: '#ffd369' }} /> Privacy & Control
          </Typography>
          <Typography variant="body2" sx={{ lineHeight: 1.6, color: '#1a1a1a' }}>
            You decide when tracking runs. Screenshots are stored securely and can be deleted by you or your admin at any time.
          </Typography>
        </Box>

        <Divider sx={{ mb: 4 }} />

        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: '#666' }}>
            Helpful Links
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {['Privacy Policy', 'Terms of Use', 'Docs', 'Support'].map(link => (
               <Link key={link} href="#" underline="hover" sx={{ color: '#1976d2' }}>{link}</Link>
            ))}
          </Box>
        </Box>
      </Drawer>
    </Box>
  );
};
